import React, { useCallback, useEffect, useState } from 'react';
import { Collapse, Nav, Navbar, NavbarToggler, NavItem } from 'reactstrap';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchQuery } from 'search/SearchActions';
import RouterPaths from '../../../constants/RouterPaths';
import { logoutUser } from '../../../auth/AuthActions';
import NavbarLoader from '../NavbarLoader/NavbarLoader';
import { InformationForUsersList } from '../../../constants/FooterNavList';
import CommonButton from '../CommonButton';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';

import logo from '../../../assets/img/logo.svg';
import authLogo from '../../../assets/img/logo-auth.svg';
import addIcon from '../../../assets/img/icons/add_circle.svg';
import listIcon from '../../../assets/img/icons/list.svg';
import closeIcon from '../../../assets/img/icons/close.svg';
import { ACTIVE_ROUTES_MAPPING } from '../../../constants/ActiveRouts';
import useWindowSize from '../../hooks/useWindowSize';
import { scrollIntoView } from '../../helpers/scrollIntoView';

function shouldHideHeader(pathname) {
  return (
    pathname === '/settings' ||
    pathname === '/add-vehicle' ||
    /^\/add-vehicle\/.+$/.test(pathname)
  );
}

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.get('isAuthenticated'));
  const roles = {
    buyer: useSelector((state) => state.auth.get('isBuyer')),
    seller: useSelector((state) => state.auth.get('isSeller')),
  };

  const windowSize = useWindowSize(0);

  useEffect(() => {
    if (windowSize.width >= 1425) setIsOpen(false);
  }, [windowSize]);

  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const handleNavClick = (id) => {
    scrollIntoView(id, navigate);
  };

  const isActiveRoute = useCallback(
    (route) =>
      ACTIVE_ROUTES_MAPPING[location.pathname] &&
      ACTIVE_ROUTES_MAPPING[location.pathname] === route,
    [location],
  );

  const dashboardRoutes = [
    RouterPaths.MY_VEHICLES,
    RouterPaths.MY_VEHICLES_DETAILS,
    RouterPaths.MY_BIDS,
    RouterPaths.MY_BIDS_DETAILS,
    RouterPaths.FAVOURITE_OFFERS,
    RouterPaths.ANALYTICS,
  ];

  const isDashboardActive = React.useMemo(() => {
    const path = location.pathname;
    return dashboardRoutes.some((route) => isActiveRoute(route) || path.startsWith(route));
  }, [location.pathname, isActiveRoute]);

  const [from, setFrom] = useState('');
  useEffect(() => {
    const redirectTo = location?.pathname;
    if (redirectTo !== '/') setFrom(redirectTo);
  }, [location]);

  return (
    <header
      className={`styled-header ${isAuthenticated ? 'header-auth' : 'header-unauth'}`}
      style={shouldHideHeader(location.pathname) ? { position: 'static' } : {}}
    >
      <Navbar expand="lg" className="header__navbar h-100">
        <Link
          to={isAuthenticated ? RouterPaths.SEARCH : RouterPaths.HOMEPAGE}
          onClick={() => dispatch(setSearchQuery(''))}
          className="page-logo-link"
        >
          <img src={isAuthenticated ? authLogo : logo} alt="logo" />
        </Link>

        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
        <>
          {isAuthenticated ? (
            <Collapse isOpen={isOpen} navbar className="header-navbar-wrapper h-100">
              <Nav
                navbar
                className="d-flex flex-row align-items-center justify-content-center header-nav w-100 h-100"
              >
                <NavItem className="header-nav__item h-100 mx-auto">
                  <NavLink
                    to={RouterPaths.DASHBOARD}
                    onClick={() => setIsOpen(false)}
                    className={`header-nav__item-link d-flex justify-content-center align-items-center h-100 ${
                      isDashboardActive ? 'active' : ''
                    }`}
                  >
                    <span className="text-uppercase">DASHBOARD</span>
                  </NavLink>
                  <NavLink
                    to={RouterPaths.SEARCH}
                    onClick={() => setIsOpen(false)}
                    className={`header-nav__item-link h-100 d-flex justify-content-center align-items-center h-100 
                      ${isActiveRoute(RouterPaths.SEARCH) ? 'active' : ''}`}
                  >
                    OFFERS
                  </NavLink>

                  <NavLink
                    to={RouterPaths.SETTINGS}
                    onClick={() => setIsOpen(false)}
                    className={`header-nav__item-link d-flex justify-content-center align-items-center h-100 ${
                      isActiveRoute(RouterPaths.SETTINGS) ? 'active' : ''
                    }`}
                  >
                    SETTINGS
                  </NavLink>
                </NavItem>
                <NavItem className="header-nav__item ml-auto d-flex">
                  {roles?.seller ? (
                    <CommonButton
                      className="text-sm"
                      handleClick={() => {
                        window.location.href = RouterPaths.ADD_VEHICLE; // window.location is intentional and required here.
                      }}
                      variant={CommonButtonVariants.RED}
                      label="ADD AUCTION"
                      icon={addIcon}
                      iconAlt="Add Vehicle"
                      iconLeft={true}
                    />
                  ) : (
                    ''
                  )}
                  <CommonButton
                    className="ms-3 text-sm"
                    handleClick={() => {
                      dispatch(logoutUser());
                    }}
                    variant={CommonButtonVariants.OUTLINED}
                    label="LOG OUT"
                  />
                </NavItem>
              </Nav>
            </Collapse>
          ) : (
            <Collapse isOpen={isOpen} navbar className="header-navbar-wrapper h-100">
              <Nav
                navbar
                className="d-flex flex-row align-items-center justify-content-center header-nav w-100 h-100"
              >
                <NavItem className="header-nav__item mx-auto d-flex justify-content-between h-100">
                  <Link
                    to="#buy-car"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('buy-car');
                      setIsOpen(false);
                    }}
                    className="header-nav__item-link font-weight-bold h-100 d-flex justify-content-center align-items-center h-100 scroller"
                  >
                    BUY CARS
                  </Link>
                  <Link
                    to="#sell-car"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('sell-car');
                      setIsOpen(false);
                    }}
                    className="header-nav__item-link font-weight-bold d-flex justify-content-center align-items-center h-100 scroller"
                  >
                    SELL CARS
                  </Link>
                  <Link
                    to="#contact-us"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick('contact-us');
                      setIsOpen(false);
                    }}
                    className="header-nav__item-link font-weight-bold d-flex justify-content-center align-items-center h-100"
                  >
                    <span className="text-uppercase font-weight-bold">
                      {InformationForUsersList.contactUs.label}
                    </span>
                  </Link>
                </NavItem>
                <NavItem className="ml-auto header-nav__item d-flex">
                  <NavLink
                    to={RouterPaths.REGISTER}
                    className="common-button primary-button auth-button"
                    activeClassName="auth-button--active"
                  >
                    Sign up
                  </NavLink>
                  <NavLink
                    to={RouterPaths.LOGIN}
                    className="common-button outline-primary auth-button ms-2"
                    activeClassName="auth-button--active"
                    state={{ from }}
                  >
                    Log In
                  </NavLink>
                </NavItem>
              </Nav>
            </Collapse>
          )}
        </>
        <NavbarToggler onClick={toggle}>
          {!isOpen ? (
            <img src={listIcon} alt="Hamburger menu icon" />
          ) : (
            <img src={closeIcon} alt="Close icon" />
          )}
        </NavbarToggler>
      </Navbar>
      <NavbarLoader />
    </header>
  );
}

export default Header;
